import './AddFeed.css';
import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { FlowForm, getKeyHandler, Stack } from 't-lib-ui/structure';
import { TextButton, Spinner, TabButtons, SvgButton } from 't-lib-ui/widgets';
import CheckmarkCircleIcon from './CheckmarkCircleIcon';
import { RouterContext, goTo, useParams, useLocation } from 't-lib-ui/router';
import ChevronLeftIcon from './ChevronLeftIcon';
import RotatingMessage from './RotatingMessage';
import FeedTile from './FeedTile';
import FeedIcon from './FeedIcon';
import FeedPreview from './FeedPreview';
import PreviewFeedPopup from './PreviewFeedPopup';
import CategoryTile from './CategoryTile';
import { CategoryIcon, categoryColorMap } from './CategoryIcons';
import { formatCategory } from '../utils/formatting';
import { AppState } from '../context/general';
import { nonNull } from 't-lib-ui/helpers';
import { requestFeed, subscribeWithMode, useGetCatalogCategories, useGetCatalogByCategory, useSearchCatalog, getPublicFeed, getPublicFeedByUrl, useGetDiscoverOnboarding } from '../connector/api';
import { RestUserFeed, RestCatalogAutocomplete, RestCatalog, CachedFeedResult, DiscoverOnboardingCollection } from '../schema-copy/api-schema';
import { getPlanLimits } from '../schema-copy/userPlans';
import { CategoryWithSubcategories } from '../schema-copy/feed-schema';
import { QueryStatus } from 't-lib-ui';
import { FetchError } from 't-lib-ui/connector';
import { isSuperuser } from '../schema-copy/capabilities';
import { getDefaultIconUrl } from '../schema-copy/default-icon';
import { autocomplete, autocompleteFetch } from '../connector/autocomplete-engine';
import { normalizeForAutocomplete } from '../schema-copy/autocomplete-helper';
import { isPrivateOnlyUrl, getRssTunnelDomain, isNewsGoogleUrl, isRedditUrl, REDDIT_UNSUPPORTED_MESSAGE } from '../schema-copy/feed-privacy';
import { checkFeedUrlSafety, feedUrlRejectionMessage } from '../schema-copy/feed-url-safety';
import { isYoutubeUrl } from '../utils/url';
import { flushPendingPreferences } from '../connector/preferences';
import { markFeedAsNew } from '../connector/post-engine';
import FeedQuotaDialog from './FeedQuotaDialog';
import FeedCreationLimitDialog from './FeedCreationLimitDialog';
import FeedQuotaNote from './FeedQuotaNote';
import { useFeedQuota } from '../connector/feed-quota';
import MailIcon from './MailIcon';
import PlusIcon from './PlusIcon';

// Shown when a YouTube channel/video fails to add. YouTube's videos.xml feeds fail
// intermittently (rate limiting, flaky endpoints), so we never blacklist them
// (see sync-server writer.ts) and tell the user to try again later instead of
// showing the generic "feed not found / broken" error.
const YOUTUBE_UNRELIABLE_MESSAGE = 'YouTube feeds are unreliable and sometimes fail even for valid channels. Please try adding this again in a little while.';

// True for any YouTube page or share link (youtube.com / youtu.be).
const isYoutubeInput = (url: string | null | undefined): boolean => {
  if (!url) return false;
  if (isYoutubeUrl(url)) return true;
  try {
    return new URL(url).hostname.toLowerCase() == 'youtu.be';
  } catch {
    return false;
  }
};

interface AddFeedProps {
  onFeedAdded: (feedId: string) => void;
  autoFocus?: boolean;
}

const AddFeed: React.FC<AddFeedProps> = ({ onFeedAdded, autoFocus }) => {
  const { category: categoryParam, subCategory } = useParams<{ category?: string; subCategory?: string }>();
  const category = categoryParam && subCategory ? `${categoryParam}/${subCategory}` : categoryParam;

  // Check if this is a feed view (feed/<feedId>)
  const isFeedView = categoryParam == 'feed';
  const feedId = isFeedView ? subCategory : undefined;
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const isAdmin = isSuperuser(appState.state.authData?.selectedMembership?.capabilities);
  const canUseNewsletters = getPlanLimits(appState.state.currentUser?.userPlan ?? 'free').maxNewsletterFeeds > 0;
  const { atLimit: atFeedQuota } = useFeedQuota();
  const [searchInput, setSearchInput] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [feedInfo, setFeedInfo] = useState<RestUserFeed | null>(null);
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState<'popular' | 'category' | 'onboarding'>(category ? 'category' : 'popular'); // 'popular': Browse Popular, 'category': Browse by Category, 'onboarding': Topics
  const [processingFeeds, setProcessingFeeds] = useState<Set<string>>(new Set());
  const [onboardingChoice, setOnboardingChoice] = useState<Record<string, 'none' | 'little' | 'full'>>({});
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedAutocompleteIndex, setSelectedAutocompleteIndex] = useState(-1);
  const [catalogAutocomplete, setCatalogAutocomplete] = useState<RestCatalogAutocomplete>([]);
  const [isLoadingAutocomplete, setIsLoadingAutocomplete] = useState(false);
  const [previewFeed, setPreviewFeed] = useState<RestUserFeed | RestCatalog | null>(null);
  const [searchedUrl, setSearchedUrl] = useState<string | null>(null);
  const [isStatusError, setIsStatusError] = useState(false);
  const [showQuotaDialog, setShowQuotaDialog] = useState(false);
  const [showCreationLimitDialog, setShowCreationLimitDialog] = useState(false);
  const [cachedFeed, setCachedFeed] = useState<CachedFeedResult | null>(null);
  const [isReadyAction, setIsReadyAction] = useState(false);

  const [subscribedFeedIds, setSubscribedFeedIds] = useState<Set<string>>(() => new Set(appState.state.currentUser?.feeds ?? []));
  const categoriesQuery = useGetCatalogCategories();

  useEffect(() => {
    const update = () => setSubscribedFeedIds(new Set(appState.feedEngine.getFeeds().map(f => f.id)));
    return appState.feedEngine.onFeedsChange(update);
  }, [appState.feedEngine]);

  const isSubscribed = useCallback((feedId: string) =>
    subscribedFeedIds.has(feedId) || appState.feedEngine.isRecentlySubscribed(feedId)
  , [subscribedFeedIds, appState.feedEngine]);

  const popularFeeds = useGetCatalogByCategory(10, undefined, { enabled: selectedTab == 'popular' });
  const categoryFeeds = useGetCatalogByCategory(10, category, { enabled: selectedTab == 'category' });
  const searchFeeds = useSearchCatalog(10, searchQuery, { enabled: searchActive && !!searchQuery });
  // Only fetched the first time the Topics tab is opened, then cached
  const onboarding = useGetDiscoverOnboarding({ enabled: selectedTab == 'onboarding' });

  useEffect(() => {
    if (categoriesQuery.data) {
      setCategories(categoriesQuery.data.categories || []);
    }
  }, [categoriesQuery.data]);

  useEffect(() => {
    if (category && selectedTab == 'category')
      categoryFeeds.changeInput({ category });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, selectedTab]);

  // Sync the tab to the URL: navigating to /a/add/<category> (e.g. the back
  // link from a feed view) should land on Browse-by-Category, not on whatever
  // tab the user was last on. The feed view (/a/add/feed/<id>) doesn't count.
  useEffect(() => {
    if (category && !isFeedView)
      setSelectedTab('category');
  }, [category, isFeedView]);

  // Load feed when feedId is present
  useEffect(() => {
    if (feedId) {
      const loadFeed = async () => {
        setIsSearching(true);
        setStatusMessage('Loading feed...');
        setFeedInfo(null);
        setSearchActive(true);

        try {
          const feedOrCatalog = await getPublicFeed(feedId, appState.state.authData);
          setFeedInfo(feedOrCatalog as RestUserFeed);
          setStatusMessage('');
        } catch (error) {
          console.error('Error loading feed:', error);
          setStatusMessage('Error loading feed: ' + (error as Error).message);
        } finally {
          setIsSearching(false);
        }
      };

      loadFeed();
    }
  }, [feedId, appState.state.authData]);

  // Navigate back to /a/add when user starts typing while viewing a feed
  useEffect(() => {
    if (feedId && searchInput.trim()) {
      goTo(routerCtx, '/a/add');
    }
  }, [feedId, searchInput, routerCtx]);

  // Debounced autocomplete effect
  useEffect(() => {
    const trimmed = searchInput.trim();

    // Only fetch autocomplete for non-URL inputs that have content
    if (!trimmed || isUrlLike(trimmed)) {
      setCatalogAutocomplete([]);
      setIsLoadingAutocomplete(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        // Works for anonymous users too — the catalog autocomplete endpoint is public.
        const auth = appState.state.authData;
        const results = await autocomplete(auth, trimmed);

        if (results == 'loading') {
          // Need to fetch from network
          setIsLoadingAutocomplete(true);
          const fetchedResults = await autocompleteFetch(auth, normalizeForAutocomplete(trimmed));
          setCatalogAutocomplete(fetchedResults);
          setIsLoadingAutocomplete(false);
        } else {
          // Got cached results
          setCatalogAutocomplete(results);
          setIsLoadingAutocomplete(false);
        }
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
        setCatalogAutocomplete([]);
        setIsLoadingAutocomplete(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInput, appState.state.authData]);

  interface AutocompleteItem {
    line1: string;
    line2?: string;
    type?: 'search';
    action: () => void;
  }

  const autocompleteItems = useMemo((): AutocompleteItem[] => {
    const trimmed = searchInput.trim();
    const items: AutocompleteItem[] = [];
    // Whether the input already resolved to a URL "add" action; if so we skip
    // the catalog-search entry (there is nothing to search — it's a direct URL).
    let hasUrlAction = false;

    // Top 10 TLDs (by usage) + blog
    const topTlds = ['com', 'net', 'org', 'edu', 'gov', 'uk', 'de', 'cn', 'jp', 'fr', 'blog'];

    // Check if input starts with http:// or https://
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      hasUrlAction = true;
      items.push({
        line1: trimmed,
        line2: 'Add website or feed',
        action: () => {
          handleSearch();
          setShowAutocomplete(false);
        }
      });
    }
    // Check if input is a complete hostname (2 or 3 parts)
    // Pattern: 2-part (domain.tld) or 3-part (subdomain.domain.tld or www.domain.tld)
    else if (trimmed.match(/^[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})?$/)) {
      const twoPartPattern = /^[a-zA-Z0-9-]+\.([a-zA-Z]{2,})$/;
      const threePartPattern = /^[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+\.([a-zA-Z]{2,})$/;

      let tld: string | null = null;
      const twoPartMatch = trimmed.match(twoPartPattern);
      const threePartMatch = trimmed.match(threePartPattern);

      if (threePartMatch) {
        tld = threePartMatch[1].toLowerCase();
      } else if (twoPartMatch) {
        tld = twoPartMatch[1].toLowerCase();
      }

      if (tld && topTlds.includes(tld)) {
        hasUrlAction = true;
        items.push({
          line1: trimmed,
          line2: 'Add website',
          action: () => {
            handleSearch();
            setShowAutocomplete(false);
          }
        });
      }
    }
    // Check for category matches (if input doesn't look like a URL)
    else if (trimmed.length >= 1) {
      const searchLower = trimmed.toLowerCase();

      // Match categories that start with the search term
      const matchedCategories = categories
        .filter(cat => {
          // Get the part after the last '/' or the whole string
          const shortName = cat.includes('/') ? cat.split('/').pop()! : cat;

          // Check if the short name starts with the search term
          return shortName.toLowerCase().startsWith(searchLower);
        })
        .slice(0, 5); // Limit to 5 results

      matchedCategories.forEach(cat => {
        const formatted = formatCategory(cat);
        if (formatted) {
          items.push({
            line1: formatted.replace(/\//g, ' / '),
            line2: 'Category',
            action: () => {
              setShowAutocomplete(false);
              setSearchInput('');
              setSelectedTab('category');
              goTo(routerCtx, `/a/add/${cat}`);
            }
          });
        }
      });
    }

    // Add catalog feed results at the end
    catalogAutocomplete.forEach(([feedId, title, domain]) => {
      items.push({
        line1: title,
        line2: domain,
        action: () => {
          setShowAutocomplete(false);
          setSearchInput('');
          goTo(routerCtx, `/a/add/feed/${feedId}`);
        }
      });
    });

    // Autocomplete only covers a subset of the catalog, so always offer a full
    // search as the last entry (unless the input is a direct URL). This also
    // replaces the old "No suggestions found" placeholder.
    if (trimmed && !hasUrlAction) {
      items.push({
        line1: trimmed,
        type: 'search',
        action: () => {
          setShowAutocomplete(false);
          handleSearch();
        }
      });
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, categories, catalogAutocomplete, routerCtx]);

  useEffect(() => {
    if (!searchInput.trim()) {
      setFeedInfo(null);
      setStatusMessage('');
      setIsStatusError(false);
      setIsSearching(false);
      setSearchActive(false);
      setSearchQuery('');
      setShowAutocomplete(false);
      setSearchedUrl(null);
      setCachedFeed(null);
    } else {
      // Show autocomplete if there are suggestions OR if we're still loading
      // Don't show if searchActive (user has already performed a search)
      if (!searchActive) {
        setShowAutocomplete(autocompleteItems.length > 0 || isLoadingAutocomplete);
      }
      setSelectedAutocompleteIndex(-1);
    }
  }, [searchInput, categories, autocompleteItems.length, isLoadingAutocomplete, searchActive]);

  const isUrlLike = (input: string): boolean => {
    const trimmed = input.trim();

    // Case 1: Full URL with protocol and domain
    // Must have protocol followed by // and at least a domain with TLD
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      // Extract the part after the protocol
      const afterProtocol = trimmed.substring(trimmed.indexOf('//') + 2);
      // Check if there's at least a valid domain (word.word pattern)
      const domainPattern = /^[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+/;
      if (domainPattern.test(afterProtocol)) {
        return true;
      }
      return false;
    }

    // Case 2 & 3: 2 or 3 part domains
    // Match: example.org, nyt.com, news.ycombinator.com, etc.
    // Allow 2-part (domain.tld) or 3-part (subdomain.domain.tld) domains
    const domainPattern = /^[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)?$/;
    if (domainPattern.test(trimmed)) {
      return true;
    }

    return false;
  };

  const normalizeUrl = (input: string): string => {
    let normalized = input.trim();

    // Remove trailing space if present (from case 3)
    normalized = normalized.trimEnd();

    // Add https:// if missing
    if (!normalized.startsWith('http:') && !normalized.startsWith('https:')) {
      normalized = 'https://' + normalized;
    }

    // Add trailing slash if no path. Must go on the path, not the end of the
    // string: 'https://www.welt.de/?service=Rss' would otherwise become
    // '...?service=Rss/' and no longer resolve to the feed.
    try {
      const searchInputObj = new URL(normalized);
      if (!searchInputObj.pathname || searchInputObj.pathname == '/') {
        searchInputObj.pathname = '/';
        normalized = searchInputObj.toString();
      }
    } catch {
      // If URL parsing fails, just add trailing slash if not present
      if (!normalized.endsWith('/') && !normalized.includes('/', 8) && !normalized.includes('?') && !normalized.includes('#')) {
        normalized += '/';
      }
    }

    return normalized;
  };

  const loadingMessages = [
    'Checking feed...',
    'Loading feed...',
    'Analyzing...',
    'Almost there...',
    'Reading more...',
    'Just a bit more...',
    'Wondering...',
    'Investigating...',
    'Please wait...',
    'This is taking longer than expected...',
    'Still working on it...',
    'Hang tight...',
  ];

  const looksLikeFeedUrl = (url: string | null | undefined): boolean => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('rss') || lower.includes('feed') || lower.includes('.xml');
  };

  const getStatusMessage = (status: string, errorType?: string, url?: string | null): string => {
    switch (status) {
      case 'FETCHING':
        return 'rotating'; // Special marker for rotating messages
      case 'BLOCK':
        return 'This feed content is not supported.';
      case 'BAD':
        if (isYoutubeInput(url ?? searchedUrl))
          return YOUTUBE_UNRELIABLE_MESSAGE;
        if (errorType == 'BLOCKED' && !looksLikeFeedUrl(url ?? searchedUrl))
          return 'This site blocks our bot from checking it. You can try to locate the RSS feed URL on the site (often linked from the footer or a "feeds" page) and paste it here directly.';
        if (errorType == 'BLOCKED' || errorType == 'HTTP')
          return 'Could not connect to this feed. The URL may be incorrect or the site may be temporarily unavailable.';
        else if (errorType == 'NO_RSS')
          return 'This URL does not appear to contain an RSS feed. Please check the URL or try the site\'s homepage.';
        else if (errorType == 'PARSE')
          return 'Found a feed but could not read it. The feed format may be invalid or corrupted.';
        else
          return 'Feed not found or broken. Please check the URL and try again later.';
      case 'READY':
        return 'Feed found! Choose how to follow it.';
      case 'ACTIVE':
      case 'PRIVATE':
        return 'Feed is ready!';
      case 'UNUSED':
        return 'This feed is no longer available.';
      default:
        return `Feed status: ${status}`;
    }
  };

  const pollForFeed = async (searchInput: string, maxAttempts: number = 12): Promise<RestUserFeed | null> => {
    const auth = nonNull(appState.state.authData);
    const waitTimes = [3000, 2000, 2000, 2000, 3000, 3000, 4000];

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        // Wait 5 seconds between polls
        await new Promise(resolve => setTimeout(resolve, waitTimes[attempt - 1] || 5000));
      }

      try {
        const result = await requestFeed(auth, searchInput);

        // Check if we got a full feed (has 'id' property)
        if ('id' in result) {
          // The feed was fetched moments ago, so its current.dz may still be on its way to the
          // CDN. Read it from the sync instance (with ?w) instead of 404-ing against the CDN.
          markFeedAsNew(result.id);
          return result as RestUserFeed;
        }

        // Check status and abort if needed
        const status = (result as any).feedStatus;
        const errorType = (result as any).errorType;
        if (status == 'READY') {
          setCachedFeed((result as any).cachedFeed);
          setStatusMessage(getStatusMessage(status, undefined, searchInput));
          return null;
        }
        if (status == 'BLOCK' || status == 'BAD') {
          setStatusMessage(getStatusMessage(status, errorType, searchInput));
          return null;
        }

        // Update status message for transitional states
        setStatusMessage(getStatusMessage(status, errorType, searchInput));
      } catch (error) {
        if (error instanceof FetchError && error.type == 'InvalidRequest') {
          setStatusMessage('We could not find an RSS or similar feed at this location.');
          setIsStatusError(true);
          return null;
        }
        console.error('Error polling feed:', error);
        setStatusMessage('Error checking feed status. Retrying...');
      }
    }

    return null;
  };

  // The server rejected a subscribe with 'AlreadySubscribed': the subscription exists (typically a
  // first attempt that looked like it failed but went through), so there is nothing left to create.
  // Adopt it locally and present it as a completed subscribe instead of an error.
  const finishAlreadySubscribed = async (subscribedFeedId?: string) => {
    if (!subscribedFeedId) {
      // No feed id to work with (older server) — at least don't call it an error.
      setCachedFeed(null);
      setStatusMessage('You are already following this feed.');
      setIsStatusError(false);
      return;
    }
    markFeedAsNew(subscribedFeedId);
    const feed = await appState.feedEngine.adoptExistingSubscription(subscribedFeedId);
    if (feed) setFeedInfo(feed);
    setCachedFeed(null);
    setStatusMessage(feed ? '' : 'You are already following this feed.');
    setIsStatusError(false);
    onFeedAdded(subscribedFeedId);
  };

  const handleAutocompleteSelect = (index: number) => {
    if (index >= 0 && index < autocompleteItems.length) {
      autocompleteItems[index].action();
    }
  };

  // for search input / autocomplete:
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showAutocomplete && autocompleteItems.length > 0) {
      // Handle autocomplete navigation
      if (e.key == 'ArrowDown') {
        e.preventDefault();
        setSelectedAutocompleteIndex(prev =>
          prev < autocompleteItems.length - 1 ? prev + 1 : prev
        );
        return;
      } else if (e.key == 'ArrowUp') {
        e.preventDefault();
        setSelectedAutocompleteIndex(prev => prev > -1 ? prev - 1 : prev);
        return;
      } else if (e.key == 'Enter') {
        e.preventDefault();
        if (selectedAutocompleteIndex < 0) {
          setShowAutocomplete(false);
          handleSearch();
        } else {
          handleAutocompleteSelect(selectedAutocompleteIndex);
        }
        return;
      } else if (e.key == 'Escape') {
        setShowAutocomplete(false);
        return;
      }
    }

    // If not handling autocomplete, use FlowForm navigation
    const flowHandler = getKeyHandler({
      enterBehaviour: 'action',
      spaceBehaviour: 'ignore',
      sidewaysBehaviour: 'leftAtBeginning',
      onAction: () => handleSearch()
    });
    flowHandler(e as any);
  };


  const handleSearch = async (inputOverride?: string) => {
    let input = inputOverride ?? searchInput;
    if (!input) return;

    // Auto-decode URL-encoded URLs (e.g. pasted from encoded contexts)
    const trimmed = input.trim();
    const trimmedLower = trimmed.toLowerCase();
    if (trimmedLower.startsWith('https%3a%2f%2f') || trimmedLower.startsWith('http%3a%2f%2f')) {
      input = decodeURIComponent(trimmed);
      setSearchInput(input);
    }

    setShowAutocomplete(false);
    setSearchActive(true);

    if (!isUrlLike(input)) {
      // Trigger catalog search
      setFeedInfo(null);
      setStatusMessage('');
      setIsSearching(false);
      const trimmedQuery = input.trim();
      setSearchQuery(trimmedQuery);
      searchFeeds.changeInput({ query: trimmedQuery });
      return;
    }

    // For URL-like inputs, proceed with feed request
    setSearchQuery('');

    // Refuse unsafe URLs (IP/localhost/.local hosts, non-standard ports, downread.com)
    // before hitting the server — the API enforces the same rules.
    const safetyRejection = checkFeedUrlSafety(normalizeUrl(input));
    if (safetyRejection) {
      setStatusMessage(feedUrlRejectionMessage(safetyRejection));
      setIsStatusError(true);
      setIsSearching(false);
      setFeedInfo(null);
      return;
    }

    // Reddit rate-limits RSS too aggressively for us to poll — only admins may still add it.
    if (isRedditUrl(normalizeUrl(input)) && !isAdmin) {
      setStatusMessage(REDDIT_UNSUPPORTED_MESSAGE);
      setIsStatusError(true);
      setIsSearching(false);
      setFeedInfo(null);
      return;
    }

    setIsSearching(true);
    setStatusMessage('Searching for feed...');
    setIsStatusError(false);
    setFeedInfo(null);
    setSearchedUrl(normalizeUrl(input));

    // Anonymous users can only follow feeds that already exist in our catalog — they can't create
    // new feeds by URL (that needs an account). Look the URL up; show it if it exists.
    if (!appState.state.authData) {
      try {
        const feed = await getPublicFeedByUrl(normalizeUrl(input));
        setFeedInfo(feed as RestUserFeed);
        setStatusMessage('');
      } catch {
        setStatusMessage("We don't have this feed yet. Sign in to add new feeds by URL.");
        setIsStatusError(true);
      } finally {
        setIsSearching(false);
      }
      return;
    }

    try {
      const normalizedUrl = normalizeUrl(input);
      const auth = nonNull(appState.state.authData);

      // Initial request
      let result;
      try {
        result = await requestFeed(auth, normalizedUrl);
      } catch (e) {
        // YouTube feeds fail intermittently; surface the "try again later" message rather
        // than the generic rejection/invalid-request text.
        if (e instanceof FetchError && (e.type == 'FeedRejected' || e.type == 'InvalidRequest') && isYoutubeInput(normalizedUrl)) {
          setStatusMessage(YOUTUBE_UNRELIABLE_MESSAGE);
          setIsStatusError(true);
          setIsSearching(false);
          return;
        }
        if (e instanceof FetchError && e.type == 'FeedRejected') {
          const reason = (e as any).extraData?.reason;
          if (reason == 'illegal')
            setStatusMessage('This feed contains illegal content and cannot be added.');
          else if (reason == 'no-rss')
            setStatusMessage('This URL does not appear to contain an RSS or Atom feed.');
          else if (reason == 'reddit')
            setStatusMessage(REDDIT_UNSUPPORTED_MESSAGE);
          else if (reason == 'private-only')
            setStatusMessage('This feed appears to be personal. Please add it as a private feed.');
          else
            setStatusMessage(e.message || 'This feed cannot be added.');
          setIsStatusError(true);
          setIsSearching(false);
          return;
        }
        if (e instanceof FetchError && e.type == 'InvalidRequest') {
          setStatusMessage(e.message || 'We could not find an RSS or similar feed at this location.');
          setIsStatusError(true);
          setIsSearching(false);
          return;
        }
        throw e;
      }

      // Check if we got a full feed immediately (ACTIVE or PRIVATE)
      if ('id' in result) {
        setFeedInfo(result as RestUserFeed);
        setStatusMessage('');
        setIsSearching(false);
        return;
      }

      // READY: show decision UI immediately
      const initialStatus = (result as any).feedStatus;
      if (initialStatus == 'READY') {
        setCachedFeed((result as any).cachedFeed);
        setStatusMessage(getStatusMessage('READY', undefined, normalizedUrl));
        setIsSearching(false);
        return;
      }

      // Otherwise, start polling
      const errorType = (result as any).errorType;
      setStatusMessage(getStatusMessage(initialStatus, errorType, normalizedUrl));

      const feed = await pollForFeed(normalizedUrl);

      if (feed) {
        setFeedInfo(feed);
        setStatusMessage('');
      } else if (!cachedFeed) {
        if (isYoutubeInput(normalizedUrl)) {
          setStatusMessage(YOUTUBE_UNRELIABLE_MESSAGE);
          setIsStatusError(true);
        } else {
          // Only show timeout message if we didn't transition to READY during polling
          setStatusMessage(prevMessage => {
            if (prevMessage && prevMessage !== 'Searching for feed...' && !prevMessage.startsWith('Feed status:'))
              return prevMessage;
            return 'The feed could not be retrieved after 60 seconds. Please try again later.';
          });
        }
      }
    } catch (error) {
      console.error('Error requesting feed:', error);
      setStatusMessage('Error: ' + (error as Error).message);
    } finally {
      setIsSearching(false);
    }
  };

  // Deep-link support: /a/add?url=<feed url> (used by the public /add page once a user
  // logs in/signs up). Pre-fill the search box and run the normal discovery flow once.
  const urlParam = useLocation().searchParams.get('url') ?? '';
  useEffect(() => {
    if (!urlParam || isFeedView) return;
    setSearchInput(urlParam);
    handleSearch(urlParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParam]);

  const handleOnboardingFeedAction = async (fid: string, currentlySubscribed: boolean) => {
    if (!appState.feedEngine) return;
    setProcessingFeeds(prev => new Set(prev).add(fid));
    try {
      const minDelay = new Promise(resolve => setTimeout(resolve, 750));
      if (currentlySubscribed) {
        await appState.feedEngine.unsubscribe(fid);
      } else {
        await flushPendingPreferences();
        await appState.feedEngine.subscribe(fid);
        onFeedAdded(fid);
      }
      await minDelay;
    } catch (error) {
      const errType = error && typeof error == 'object' && 'type' in error ? (error as any).type : undefined;
      if (errType == 'Quota')
        setShowQuotaDialog(true);
      else if (errType == 'FeedCreationLimit')
        setShowCreationLimitDialog(true);
      else
        console.error('Subscription action failed:', error);
    } finally {
      setProcessingFeeds(prev => {
        const next = new Set(prev);
        next.delete(fid);
        return next;
      });
    }
  };

  // Renders a single feed row (icon + name + follow/unfollow) like the
  // collection feeds on DiscoverPage.
  const renderOnboardingFeed = (feed: DiscoverOnboardingCollection['baseFeed']) => {
    const subscribed = isSubscribed(feed.feedId);
    const isProcessing = processingFeeds.has(feed.feedId);
    return (
      <div key={feed.feedId} className="onboardingFeed">
        <FeedIcon className="onboardingFeedIcon" src={feed.iconUrl} circular={feed.circularIcon} />
        <a className="onboardingFeedName" href={feed.siteUrl} target="_blank" rel="noopener noreferrer">{feed.title}</a>
        <div className="onboardingFeedActions">
          <Stack>
            <Spinner spinning={isProcessing} className={isProcessing ? 'show' : ''} />
            <CheckmarkCircleIcon className={subscribed && !isProcessing ? 'show' : ''} />
          </Stack>
          <TextButton
            label={isProcessing ? 'working' : subscribed ? 'Unfollow' : '+ Follow'}
            type="major"
            size="small"
            enabled={isProcessing || (atFeedQuota && !subscribed) ? 'disabled' : 'enabled'}
            onClick={() => handleOnboardingFeedAction(feed.feedId, subscribed)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="addFeed">
      <FlowForm main={true}>
        <div className="addFeedContainer">
          <div className="addFeedContent">
            {atFeedQuota && (
              <div className="feedQuotaBanner"><FeedQuotaNote /></div>
            )}
            {feedId && (
              <button
                className="addFeedBackLink flowFocussable"
                tabIndex={0}
                onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
                onClick={() => {
                  const cat = feedInfo?.category;
                  goTo(routerCtx, cat ? `/a/add/${cat}` : '/a/add');
                }}
              >
                <ChevronLeftIcon size={18} />
                <span>{(feedInfo?.category && formatCategory(feedInfo.category)?.replace(/\//g, ' / ')) || 'Browse'}</span>
              </button>
            )}
            {!searchActive && !feedId && !category && (
              <div className="addFeedIntro">
                <span className="addFeedIntroIcon"><PlusIcon size={24} /></span>
                <div className="addFeedIntroText">
                  <h2 className="addFeedIntroTitle">Find feeds to follow</h2>
                  <p className="addFeedIntroSubtitle">Search by website, topic, or RSS feed URL.</p>
                </div>
              </div>
            )}
            <div
              className={`addFeedSearchWrapper ${showAutocomplete && autocompleteItems.length > 0 ? 'withAutocomplete' : ''}`}
            >
              <svg className="addFeedSearchIcon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'var(--gray-500)' }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Website or topic or RSS URL"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  if (searchActive && !isSearching) {
                    setSearchActive(false);
                    setSearchQuery('');
                    setFeedInfo(null);
                    setStatusMessage('');
                    setSearchedUrl(null);
                  }
                }}
                onKeyDown={handleInputKeyDown}
                onBlur={() => {
                  // Close autocomplete when input loses focus
                  setTimeout(() => {
                    setShowAutocomplete(false);
                  }, 200);
                }}
                className="addFeedInput flowFocussable"
                tabIndex={0}
                autoFocus={autoFocus}
              />
              {searchInput && (
                <svg
                  className="addFeedClearIcon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  style={{ color: 'var(--gray-500)' }}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  onClick={() => {
                    setSearchInput('');
                    setSearchActive(false);
                    setSearchQuery('');
                    setFeedInfo(null);
                    setStatusMessage('');
                  }}
                >
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              )}
              {showAutocomplete && autocompleteItems.length > 0 && (
                <div className="addFeedAutocomplete">
                  {autocompleteItems.map((item, index) => {
                    if (item.type == 'search')
                      return (
                        <React.Fragment key={index}>
                          {isLoadingAutocomplete && (
                            <div className="addFeedAutocompleteItem addFeedAutocompleteLoading">
                              <Spinner spinning={true} />
                              <span>Loading more...</span>
                            </div>
                          )}
                          <div
                            className={`addFeedAutocompleteItem addFeedAutocompleteSearch ${index == selectedAutocompleteIndex ? 'selected' : ''}`}
                            onClick={() => handleAutocompleteSelect(index)}
                            onMouseEnter={() => setSelectedAutocompleteIndex(index)}
                          >
                            <svg className="addFeedAutocompleteSearchIcon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="11" cy="11" r="8"/>
                              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                            <span className="addFeedAutocompleteSearchLabel">Search for “{item.line1}”</span>
                          </div>
                        </React.Fragment>
                      );
                    return (
                      <div
                        key={index}
                        className={`addFeedAutocompleteItem ${index == selectedAutocompleteIndex ? 'selected' : ''}`}
                        onClick={() => handleAutocompleteSelect(index)}
                        onMouseEnter={() => setSelectedAutocompleteIndex(index)}
                      >
                        <div className="addFeedAutocompleteLine1">{item.line1}</div>
                        {item.line2 && <div className="addFeedAutocompleteLine2">{item.line2}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {statusMessage && !cachedFeed && (
              <div className={`addFeedStatus${isStatusError ? ' error' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {isSearching && <Spinner spinning={true} />}
                <span>
                  {statusMessage === 'rotating' ? (
                    <RotatingMessage messages={loadingMessages} intervalMs={2000} />
                  ) : statusMessage.includes('does not appear to contain an RSS feed') && searchedUrl ? (
                    <>
                      {(() => {
                        try {
                          const url = new URL(searchedUrl);
                          const domain = url.hostname;
                          const homepageUrl = `${url.protocol}//${url.hostname}/`;
                          return (
                            <>
                              This URL does not appear to contain an RSS feed. Please check the URL or try{' '}
                              <a href={homepageUrl} target="_blank" rel="noopener noreferrer">{domain}</a>.
                            </>
                          );
                        } catch {
                          return statusMessage;
                        }
                      })()}
                    </>
                  ) : (
                    statusMessage
                  )}
                </span>
              </div>
            )}
            {cachedFeed && !feedInfo && (
              <div className="addFeedReadyDecision">
                {/* Feed preview card (FeedTile-like) */}
                <div className="readyFeedCard feedTile">
                  <div className="feedTileHeader withImage">
                    {/* The feed isn't created yet (no CDN image), so preview with the generated default icon. */}
                    <img src={getDefaultIconUrl(cachedFeed.shortTitle || cachedFeed.title || '')} alt={cachedFeed.title} className={`feedTileImage${cachedFeed.circularIcon ? ' feedIconCircular' : ''}`} />
                    <div className="feedTileHeaderBody">
                      <div className="feedTileHeaderTop">
                        <div className="feedTileHeaderText">
                          <h3 className="feedTileTitle">{cachedFeed.shortTitle || cachedFeed.title}</h3>
                          {cachedFeed.siteUrl && (
                            <p className="feedTileUrl">{(() => { try { return new URL(cachedFeed.siteUrl).hostname; } catch { return cachedFeed.siteUrl; } })()}</p>
                          )}
                        </div>
                      </div>
                      {cachedFeed.description && (
                        <div className="feedTileDescriptions">
                          <p className="feedTileDescription">{cachedFeed.description.length > 200 ? cachedFeed.description.slice(0, 200) + '…' : cachedFeed.description}</p>
                        </div>
                      )}
                      {cachedFeed.copyright && (
                        <p className="readyFeedCopyright">{/copyright/i.test(cachedFeed.copyright) ? '' : 'Copyright: '}{cachedFeed.copyright}</p>
                      )}
                    </div>
                  </div>
                  {cachedFeed.category && (
                    <div className="feedTileMetadata">
                      <span className="feedTileMetaItem feedTileMetaCategory">
                        <CategoryIcon category={cachedFeed.category} size={16} color="var(--gray-400)" />
                        {formatCategory(cachedFeed.category)}
                      </span>
                    </div>
                  )}
                  {cachedFeed.precheckFailure == 'adult' && (
                    <div style={{ padding: '8px 16px', margin: '0 16px', background: '#fff3cd', borderRadius: '6px', fontSize: '0.85rem', color: '#856404', border: '1px solid #ffc107' }}>
                      This feed has been flagged as potentially containing adult content. You can add it as a private feed. Adding it as a public feed requires admin review. Adult content is not allowed.
                    </div>
                  )}
                  {isPrivateOnlyUrl(searchedUrl ?? cachedFeed.url) && (() => {
                    const privateOnlyUrl = searchedUrl ?? cachedFeed.url;
                    const tunnelDomain = getRssTunnelDomain(privateOnlyUrl);
                    // Google News is an expected, well-understood case rather than a problem with the
                    // feed, so it gets a neutral informational note instead of the yellow warning.
                    const isNews = isNewsGoogleUrl(privateOnlyUrl);
                    const noticeStyle = isNews
                      ? { padding: '8px 16px', margin: '0 16px', background: 'var(--dr-bg-panel)', borderRadius: '6px', fontSize: '0.85rem', color: 'var(--gray-300)', border: '1px solid var(--dr-border-panel)' }
                      : { padding: '8px 16px', margin: '0 16px', background: '#fff3cd', borderRadius: '6px', fontSize: '0.85rem', color: '#856404', border: '1px solid #ffc107' };
                    return (
                      <div style={noticeStyle}>
                        {isNews ? (
                          <>
                            <p style={{ margin: '0 0 6px 0' }}>
                              Google News feeds (news.google.com) are personalized topic and search results, not a
                              publisher's own feed, so they are always added as private feeds.
                            </p>
                            <p style={{ margin: 0 }}>
                              Only you can see it, and it counts against your private feed quota.
                            </p>
                          </>
                        ) : tunnelDomain ? (
                          <>
                            <p style={{ margin: '0 0 6px 0' }}>
                              Unfortunately, we can't publish {tunnelDomain} tunnels as public feeds. They repackage another site's content as personalized content, so they can only be added as a private feed.
                            </p>
                            <p style={{ margin: '0 0 6px 0' }}>
                              If the original site offers its own RSS or Atom feed, we recommend adding that directly — without going through {tunnelDomain}.
                            </p>
                            <p style={{ margin: 0 }}>
                              If you run into any issues with an RSS feed on Downread, please get in touch with us.
                            </p>
                          </>
                        ) : (
                          'This feed appears to be personalized (e.g. it uses URL parameters) and can only be added as a private feed.'
                        )}
                      </div>
                    );
                  })()}
                  {/* Action buttons */}
                  <div className="readyFeedActions">
                    {isReadyAction
                      ? <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0' }}><Spinner spinning={true} /><span>Subscribing...</span></div>
                      : <>
                        {!isPrivateOnlyUrl(searchedUrl ?? cachedFeed.url) && (
                        <button className={`readyOption readyOptionPublic flowFocussable${atFeedQuota ? ' readyOptionDisabled' : ''}`} tabIndex={0} disabled={atFeedQuota} onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any} onClick={async () => {
                          if (atFeedQuota) return;
                          setIsReadyAction(true);
                          try {
                            const auth = nonNull(appState.state.authData);
                            const result = await subscribeWithMode(auth, searchedUrl ?? cachedFeed.url, 'public');
                            markFeedAsNew(result.feed.id);
                            appState.feedEngine.handleSubscribeResult(result);
                            setFeedInfo(result.feed);
                            setCachedFeed(null);
                            setStatusMessage('');
                            onFeedAdded(result.feed.id);
                          } catch (e) {
                            if (e instanceof FetchError && e.type == 'AlreadySubscribed')
                              await finishAlreadySubscribed(e.extraData?.feedId);
                            else if (e instanceof FetchError && e.type == 'FeedCreationLimit')
                              setShowCreationLimitDialog(true);
                            else
                              setStatusMessage('Error: ' + (e as Error).message);
                          } finally {
                            setIsReadyAction(false);
                          }
                        }}>
                          <span className="readyOptionTitle">Follow as <b>public</b> feed</span>
                          <span className="readyOptionDesc">{cachedFeed.precheckFailure == 'adult'
                            ? 'Will be submitted for review before becoming visible to other users.'
                            : 'Other users can also read this feed. Most feeds are public.'
                          }</span>
                        </button>
                        )}
                        {(() => {
                          const plan = appState.state.currentUser?.userPlan ?? 'free';
                          const { maxPrivateFeeds } = getPlanLimits(plan);
                          const used = appState.feedEngine.getPrivateFeedCount();
                          const remaining = Math.max(0, maxPrivateFeeds - used);
                          const exhausted = remaining == 0;
                          return <button className={`readyOption readyOptionPrivate flowFocussable${exhausted || atFeedQuota ? ' readyOptionDisabled' : ''}`} tabIndex={0} disabled={exhausted || atFeedQuota} onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any} onClick={async () => {
                            if (exhausted || atFeedQuota) return;
                            setIsReadyAction(true);
                            try {
                              const auth = nonNull(appState.state.authData);
                              const result = await subscribeWithMode(auth, searchedUrl ?? cachedFeed.url, 'private');
                              markFeedAsNew(result.feed.id);
                            appState.feedEngine.handleSubscribeResult(result);
                              setFeedInfo(result.feed);
                              setCachedFeed(null);
                              setStatusMessage('');
                              onFeedAdded(result.feed.id);
                            } catch (e) {
                              if (e instanceof FetchError && e.type == 'AlreadySubscribed')
                                await finishAlreadySubscribed(e.extraData?.feedId);
                              else if (e instanceof FetchError && e.type == 'Quota')
                                setShowQuotaDialog(true);
                              else
                                setStatusMessage('Error: ' + (e as Error).message);
                            } finally {
                              setIsReadyAction(false);
                            }
                          }}>
                            <span className="readyOptionTitle">Follow as <b>private</b> feed</span>
                            <span className="readyOptionDesc">{exhausted
                              ? 'No private feeds left. Upgrade your plan for more.'
                              : <>Only you can see it. {remaining} private feed{remaining != 1 ? 's' : ''} left.</>
                            }</span>
                          </button>;
                        })()}
                        <button className="readyOption readyOptionSkip flowFocussable" tabIndex={0} onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any} onClick={() => {
                          setCachedFeed(null);
                          setStatusMessage('');
                          setSearchActive(false);
                          setSearchInput('');
                        }}>
                          <span className="readyOptionTitle">Do not follow this feed</span>
                        </button>
                      </>
                    }
                  </div>
                </div>
              </div>
            )}
            {!searchActive && !feedId && canUseNewsletters && (
              <button
                className="addFeedNewsletterCta flowFocussable"
                tabIndex={0}
                onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
                onClick={() => goTo(routerCtx, '/a/add/newsletter')}
              >
                <span className="addFeedNewsletterCtaIcon"><MailIcon size={26} color="currentColor" /></span>
                <span className="addFeedNewsletterCtaText">
                  <span className="addFeedNewsletterCtaTitle">Set up newsletter inbox</span>
                  <span className="addFeedNewsletterCtaDesc">Create a private email inbox and get newsletters as a feed.</span>
                </span>
              </button>
            )}
            {!searchActive && !feedId && categories.length > 0 && (
              <div className="addFeedCategories">
                <TabButtons
                  labels={['Browse Popular', 'Browse by Category', 'Interests']}
                  shortLabels={['Popular Feeds', 'Categories', 'Interests']}
                  selectedIndex={selectedTab == 'popular' ? 0 : selectedTab == 'category' ? 1 : 2}
                  onClick={index => setSelectedTab(index == 0 ? 'popular' : index == 1 ? 'category' : 'onboarding')}
                  className="addFeedTabs"
                />
                {selectedTab === 'popular' && (
                  <div className="addFeedPopularList">
                    {popularFeeds.status === 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                        <Spinner spinning={true} />
                      </div>
                    )}
                    {popularFeeds.status === 2 && popularFeeds.chunks.length === 0 && (
                      <p>No popular feeds found.</p>
                    )}
                    {popularFeeds.chunks.flatMap(chunk => chunk.items).map((catalogFeed) => (
                      <FeedTile
                        key={catalogFeed.id}
                        feed={catalogFeed}
                        subscribed={isSubscribed(catalogFeed.id)}
                        isAdmin={isAdmin}
                        showPrivacyNote={true}
                        onAction={() => {
                          // Reload user data after subscription change
                          onFeedAdded(catalogFeed.id);
                        }}
                        onPreview={() => setPreviewFeed(catalogFeed)}
                        onQuotaExceeded={() => setShowQuotaDialog(true)}
                      />
                    ))}
                    {popularFeeds.hasMore && popularFeeds.status === 2 && (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                        <TextButton
                          label="Load More"
                          type="major"
                          onClick={() => popularFeeds.loadMore()}
                        />
                      </div>
                    )}
                  </div>
                )}
                {selectedTab === 'category' && (
                  <>
                    {category && category in CategoryWithSubcategories && (() => {
                      const goToParentCategory = () => {
                        const parentCategory = category.includes('/')
                          ? category.substring(0, category.lastIndexOf('/'))
                          : null;
                        goTo(routerCtx, parentCategory ? `/a/add/${parentCategory}` : '/a/add');
                        document.querySelector('.discoverPage')?.scrollTo({ top: 0, behavior: 'smooth' });
                      };
                      return (
                      <div className="addFeedTabsNav" style={{ display: 'flex', alignItems: 'stretch' }}>
                        <SvgButton className='catBackButton'
                          onClick={goToParentCategory}
                          focussable='flow'
                        >
                          <ChevronLeftIcon size={36} />
                        </SvgButton>
                        <div
                          className="addFeedCategoryHeadline"
                          onClick={goToParentCategory}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '0 16px',
                            flex: 1,
                            cursor: 'pointer'
                          }}
                        >
                          <CategoryIcon category={category} size={36} color={categoryColorMap[category] || 'var(--gray-400)'} />
                          <span style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--gray-200)' }}>
                            {formatCategory(category)?.replace(/\//, ' / ') || category }
                          </span>
                        </div>
                      </div>
                      );
                    })()}
                    <div 
                      className={`addFeedCategoryGrid ${category ? 'subcat' : ''}`}
                    >
                      {(() => {
                        // If category is set, show subcategories from CategoryWithSubcategories
                        if (category && category in CategoryWithSubcategories) {
                          const subcategories = CategoryWithSubcategories[category as keyof typeof CategoryWithSubcategories];
                          return subcategories
                            .filter((cat) => cat !== category) // Exclude the selected category itself
                            .map((cat) => {
                              // For subcategories, strip the parent prefix for display
                              return (
                                <CategoryTile
                                  key={cat}
                                  category={cat}
                                  onCategoryClick={() => goTo(routerCtx, `/a/add/${cat}`)}
                                  small={true}
                                />
                              );
                            });
                        }
                        // Otherwise show only top-level categories (no slashes)
                        return categories
                          .filter((cat) => !cat.includes('/'))
                          .map((cat) => (
                            <CategoryTile
                              key={cat}
                              category={cat}
                              onCategoryClick={(cat) => goTo(routerCtx, `/a/add/${cat}`)}
                            />
                          ));
                      })()}
                    </div>
                    {category && (
                      <div className="addFeedCategoryFeedList" style={{ marginTop: '1rem' }}>
                        {categoryFeeds.status === QueryStatus.Loading && (
                          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                            <Spinner spinning={true} />
                          </div>
                        )}
                        {categoryFeeds.status === QueryStatus.Success && categoryFeeds.chunks.length === 0 && (
                          <p>No feeds found in this category.</p>
                        )}
                        {categoryFeeds.chunks.flatMap(chunk => chunk.items).map((catalogFeed) => (
                          <FeedTile
                            key={catalogFeed.id}
                            feed={catalogFeed}
                            subscribed={isSubscribed(catalogFeed.id)}
                            isAdmin={isAdmin}
                            showPrivacyNote={true}
                            onAction={() => {
                              onFeedAdded(catalogFeed.id);
                            }}
                            onPreview={() => setPreviewFeed(catalogFeed)}
                            onQuotaExceeded={() => setShowQuotaDialog(true)}
                          />
                        ))}
                        {categoryFeeds.hasMore && categoryFeeds.status === QueryStatus.Success && (
                          <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                            <TextButton
                              label="Load More"
                              type="major"
                              onClick={() => categoryFeeds.loadMore()}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
                {selectedTab === 'onboarding' && (
                  <div className="onboardingList">
                    {onboarding.status === QueryStatus.Loading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                        <Spinner spinning={true} />
                      </div>
                    ) : (!onboarding.data || onboarding.data.collections.length === 0) ? (
                      <p style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '2rem' }}>
                        No topics available yet.
                      </p>
                    ) : (
                      <>
                        <p className="onboardingHelp">
                          Recommended feeds grouped by interest. Pick the topics you care about and
                          choose how much you want to follow.
                        </p>
                      {[...onboarding.data.collections]
                        .sort((a, b) => (b.priority ?? -Infinity) - (a.priority ?? -Infinity))
                        .map((col) => {
                          const choice = onboardingChoice[col.id] ?? 'none';
                          const cycleChoice = () => setOnboardingChoice(prev => {
                            const order = ['none', 'little', 'full'] as const;
                            const cur = prev[col.id] ?? 'none';
                            return { ...prev, [col.id]: order[(order.indexOf(cur) + 1) % order.length] };
                          });
                          return (
                            <div key={col.id} className="onboardingCard" onClick={cycleChoice}>
                              <div className="onboardingCardHeader">
                                <h3 className="onboardingTopic">{col.topic}</h3>
                                <div className="onboardingChoices" onClick={e => e.stopPropagation()}>
                                  {([
                                    ['none', 'None'],
                                    ['little', 'A little'],
                                    ['full', 'A lot'],
                                  ] as const).map(([opt, label], i) => {
                                    const select = () => setOnboardingChoice(prev => ({ ...prev, [col.id]: opt }));
                                    return (
                                      <button
                                        key={opt}
                                        type="button"
                                        tabIndex={0}
                                        className={`onboardingChoiceBtn flowFocussable${i > 0 ? ' sidewayFocus' : ''}${choice == opt ? ' active' : ''}`}
                                        onClick={select}
                                        onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: select }) as any}
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              {choice == 'little' && (
                                <div className="onboardingFeeds" onClick={e => e.stopPropagation()}>
                                  {renderOnboardingFeed(col.baseFeed)}
                                </div>
                              )}
                              {choice == 'full' && (
                                <div className="onboardingFeeds" onClick={e => e.stopPropagation()}>
                                  {col.feeds.map(renderOnboardingFeed)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            {feedInfo && (feedId || searchActive) && (
              <FeedTile
                feed={feedInfo}
                subscribed={isSubscribed(feedInfo.id)}
                onAction={() => onFeedAdded(feedInfo.id)}
                showPrivacyNote={true}
                isAdmin={isAdmin}
                onQuotaExceeded={() => setShowQuotaDialog(true)}
              >
                <FeedPreview feed={feedInfo} maxPosts={20} />
              </FeedTile>
            )}
            {searchActive && !feedInfo && searchQuery && (
              <div className="addFeedPopularList">
                {searchFeeds.status === QueryStatus.Loading && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <Spinner spinning={true} />
                  </div>
                )}
                {searchFeeds.status === QueryStatus.Success &&
                 searchFeeds.chunks.flatMap(chunk => chunk.items).length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '3rem 2rem',
                    background: 'var(--gray-975)',
                    borderRadius: '8px',
                    margin: '1rem 0'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                    <h3 style={{
                      margin: '0 0 0.5rem 0',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: 'var(--gray-200)'
                    }}>
                      No feeds found for "{searchQuery}"
                    </h3>
                    <p style={{
                      margin: '0',
                      color: 'var(--gray-400)',
                      fontSize: '0.95rem',
                      lineHeight: 1.5
                    }}>
                      Try searching with different keywords or browse our categories to discover feeds.
                    </p>
                  </div>
                )}
                {searchFeeds.chunks.flatMap(chunk => chunk.items).map((catalogFeed) => (
                  <FeedTile
                    key={catalogFeed.id}
                    feed={catalogFeed}
                    subscribed={isSubscribed(catalogFeed.id)}
                    isAdmin={isAdmin}
                    showPrivacyNote={true}
                    onAction={() => {
                      onFeedAdded(catalogFeed.id);
                    }}
                    onPreview={() => setPreviewFeed(catalogFeed)}
                    onQuotaExceeded={() => setShowQuotaDialog(true)}
                  />
                ))}
                {searchFeeds.hasMore && searchFeeds.status === QueryStatus.Success && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                    <TextButton
                      label="Load More"
                      type="major"
                      onClick={() => searchFeeds.loadMore()}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Friendly catch-all at the very bottom of the page, below the results. Only
              shown while the user is adding by URL — that's where things go wrong (a feed
              that won't load, a site without a discoverable feed). Adding from the catalog
              just works, so we don't clutter that path with a support address. */}
          {isUrlLike(searchInput) && (
            <div className="addFeedSupportNote">
              <span className="addFeedSupportIcon"><MailIcon size={16} /></span>
              <p className="addFeedSupportText">
                Having issues with a feed? Please write to{' '}
                <a className="addFeedSupportLink flowFocussable" tabIndex={0} href="mailto:support@downread.com"
                  onKeyDown={getKeyHandler(() => { window.location.href = 'mailto:support@downread.com'; })}
                >support@downread.com</a>. A real person reads every mail and we're happy to make it work.
              </p>
            </div>
          )}
        </div>
      </FlowForm>
      <PreviewFeedPopup
        show={!!previewFeed}
        feed={previewFeed}
        onClose={() => setPreviewFeed(null)}
        subscribed={previewFeed ? isSubscribed(previewFeed.id) : false}
        onSubscribe={async () => {
          if (previewFeed && appState.feedEngine) {
            try {
              await flushPendingPreferences();
              const result = await appState.feedEngine.subscribe(previewFeed.id);
              if (result) {
                onFeedAdded(previewFeed.id);
                setPreviewFeed(null);
              }
            } catch (e) {
              const errType = e && typeof e == 'object' && 'type' in e ? (e as any).type : undefined;
              if (errType == 'Quota') {
                setPreviewFeed(null);
                setShowQuotaDialog(true);
              } else if (errType == 'FeedCreationLimit') {
                setPreviewFeed(null);
                setShowCreationLimitDialog(true);
              }
            }
          }
        }}
      />
      <FeedQuotaDialog show={showQuotaDialog} onClose={() => setShowQuotaDialog(false)} />
      <FeedCreationLimitDialog show={showCreationLimitDialog} onClose={() => setShowCreationLimitDialog(false)} />
    </div>
  );
};

export default AddFeed;
